import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent, 
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from '@mui/material';
import { PlayArrow, Pause, Stop, Delete, Save } from '@mui/icons-material';
import { getCategoryColor } from '../utils/colors';

const TIMER_STORAGE_KEY = 'ops_squid_timer_data';

const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const WorkTimer = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [taskName, setTaskName] = useState('');
  const [taskCategory, setTaskCategory] = useState('');
  const [savedTimers, setSavedTimers] = useState([]);
  const [openHistory, setOpenHistory] = useState(false);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);

  // Load saved timers from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem(TIMER_STORAGE_KEY);
    if (savedData) {
      setSavedTimers(JSON.parse(savedData));
    }
  }, []);

  // Save timers to localStorage whenever the savedTimers state changes
  useEffect(() => {
    localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(savedTimers));
  }, [savedTimers]);

  // Handle timer logic
  useEffect(() => {
    if (isRunning) {
      startTimeRef.current = Date.now() - (elapsedTime * 1000);
      intervalRef.current = setInterval(() => {
        const currentTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsedTime(currentTime);
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning]);

  const handleStartPause = () => {
    setIsRunning(!isRunning);
  };

  const handleStop = () => {
    setIsRunning(false);
    // Don't reset elapsed time - allow user to save it
  };

  const handleReset = () => {
    setIsRunning(false);
    setElapsedTime(0);
    setTaskName('');
    setTaskCategory('');
  };

  const handleSave = () => {
    if (elapsedTime === 0) return;
    
    const newTimer = {
      id: Date.now(),
      taskName: taskName || 'Unnamed Task',
      category: taskCategory || 'Uncategorized',
      duration: elapsedTime,
      timestamp: new Date().toISOString(),
      formattedDuration: formatTime(elapsedTime)
    };
    
    setSavedTimers([newTimer, ...savedTimers]);
    handleReset();
  };

  const handleDelete = (id) => {
    setSavedTimers(savedTimers.filter(timer => timer.id !== id));
  };

  const handleOpenHistory = () => {
    setOpenHistory(true);
  };

  const handleCloseHistory = () => {
    setOpenHistory(false);
  };

  return (
    <Paper elevation={0} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Task Name"
          variant="outlined"
          fullWidth
          value={taskName}
          onChange={(e) => setTaskName(e.target.value)}
          disabled={isRunning}
          sx={{ mb: 2 }}
        />
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="task-category-label">Category</InputLabel>
          <Select
            labelId="task-category-label"
            value={taskCategory}
            label="Category"
            onChange={(e) => setTaskCategory(e.target.value)}
            disabled={isRunning}
          >
            <MenuItem value="Project">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ 
                  width: 14, 
                  height: 14, 
                  borderRadius: '50%', 
                  mr: 1,
                  backgroundColor: getCategoryColor('Project') 
                }}/>
                Project
              </Box>
            </MenuItem>
            <MenuItem value="Toil">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ 
                  width: 14, 
                  height: 14, 
                  borderRadius: '50%', 
                  mr: 1,
                  backgroundColor: getCategoryColor('Toil') 
                }}/>
                Toil
              </Box>
            </MenuItem>
            <MenuItem value="Unplanned">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ 
                  width: 14, 
                  height: 14, 
                  borderRadius: '50%', 
                  mr: 1,
                  backgroundColor: getCategoryColor('Unplanned') 
                }}/>
                Unplanned
              </Box>
            </MenuItem>
            <MenuItem value="Change">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ 
                  width: 14, 
                  height: 14, 
                  borderRadius: '50%', 
                  mr: 1,
                  backgroundColor: getCategoryColor('Change') 
                }}/>
                Change
              </Box>
            </MenuItem>
            <MenuItem value="Learn">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ 
                  width: 14, 
                  height: 14, 
                  borderRadius: '50%', 
                  mr: 1,
                  backgroundColor: getCategoryColor('Learn') 
                }}/>
                Learn
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h3" component="div" sx={{ fontFamily: 'monospace', mb: 2 }}>
          {formatTime(elapsedTime)}
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button
            variant="contained"
            color={isRunning ? "warning" : "success"}
            startIcon={isRunning ? <Pause /> : <PlayArrow />}
            onClick={handleStartPause}
          >
            {isRunning ? 'Pause' : 'Start'}
          </Button>
          
          <Button
            variant="contained"
            color="error"
            startIcon={<Stop />}
            onClick={handleStop}
            disabled={!isRunning && elapsedTime === 0}
          >
            Stop
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={elapsedTime === 0}
          >
            Save
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button variant="outlined" onClick={handleOpenHistory}>
          View History
        </Button>
      </Box>
      
      {/* Timer History Dialog */}
      <Dialog 
        open={openHistory} 
        onClose={handleCloseHistory}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Timer History</DialogTitle>
        <DialogContent>
          {savedTimers.length === 0 ? (
            <Typography variant="body1">No saved timers yet.</Typography>
          ) : (
            <List>
              {savedTimers.map((timer) => (
                <ListItem key={timer.id} divider>
                  <ListItemText
                    primary={timer.taskName}
                    secondary={
                      <>
                        <Chip
                          label={timer.category}
                          size="small"
                          sx={{
                            bgcolor: getCategoryColor(timer.category),
                            color: 'white',
                            fontWeight: 'bold',
                            mr: 1
                          }}
                        />
                        <Typography component="span" variant="body2" color="text.primary">
                          {timer.formattedDuration}
                        </Typography>
                        <br />
                        <Typography component="span" variant="body2" color="text.secondary">
                          {new Date(timer.timestamp).toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" color="error" onClick={() => handleDelete(timer.id)}>
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WorkTimer; 